import { Link, Text } from "@chakra-ui/layout"
import React, { useEffect, useRef, useState } from "react"
import { withErrorBoundary } from "react-error-boundary"
import { useInView } from "react-intersection-observer"
import { useSegment } from "tracking"
import { Logger } from "lib/logger"
import type { VariantType, VariantTypeV2 } from "types"

import { ItemsWrapper, ItemsWrapperDiv, LinkItem } from "./PDPBodyIndex.styles"
import type { IndexItemProps } from "./utils/utils"
import { getAvailableItemsInIndex } from "./utils/utils"
import { colours } from "@shared/ui.design-system"

type BreadcrumbItem = {
  url: string
  label: string
}

const IndexItem: React.FC<IndexItemProps> = ({ item, activeItem }) => {
  const { id, title } = item

  const active = id === activeItem
  const { track } = useSegment()

  const itemClicked = () => {
    track({
      event: "Page Element Viewed",
      properties: {
        module_content: "PDP body index",
        module_content_details: title,
      },
    })
  }
  const borderBottomStyle = {
    borderBottom: `2px solid ${colours.accent.strong}`,
    color: colours.primary.dark,
    fontWeight: 400,
  }

  return (
    <LinkItem as="li">
      <Link href={`#${id}`} onClick={itemClicked}>
        <Text
          data-testid={id}
          variant="acuminLight"
          mt="0"
          {...(active
            ? borderBottomStyle
            : {
                color: colours.primary.light,
                fontWeight: 400,
                borderBottom: "2px solid transparent",
              })}
          _hover={{ lg: borderBottomStyle }}
        >
          {title}
        </Text>
      </Link>
    </LinkItem>
  )
}

export type VariantTypeForNewPDP = VariantType & {
  breadcrumbs?: BreadcrumbItem[]
}
export type VariantTypeV2ForNewPDP = VariantTypeV2 & {
  breadcrumbs?: BreadcrumbItem[]
}

type Props = {
  variant: VariantTypeForNewPDP
  recommendationLength?: number
}

export const PDPBodyIndexComponent: React.FC<Props> = ({
  variant,
  recommendationLength,
}: Props) => {
  const [activeItem, setActiveItem] = useState("about-this-product")
  const { track } = useSegment()
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  })
  const indexRef = useRef<HTMLUListElement | null>(null)
  const itemsInIndex = getAvailableItemsInIndex(variant, recommendationLength)

  useEffect(() => {
    const indexItem = indexRef?.current
    if (!indexItem || !indexItem?.children) return undefined

    const itemIndex = itemsInIndex.findIndex((item) => item.id === activeItem)

    if (itemIndex < 0) return undefined

    const item = indexItem?.children[itemIndex]

    const { right, left } = item.getBoundingClientRect()

    if (right > indexItem.clientWidth || left < 0) {
      indexItem.scrollTo({
        left: item.getBoundingClientRect().left,
        behavior: "smooth",
      })
    }
  }, [activeItem, itemsInIndex])

  useEffect(() => {
    const { hash } = window.location
    if (hash) {
      const targetId = hash.replace("#", "")
      const itemIndex = itemsInIndex.findIndex((item) => item.id === targetId)
      if (itemIndex) {
        setActiveItem(targetId)
        setTimeout(() => {
          document
            .getElementById(targetId)
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
        }, 0)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    window.addEventListener("scroll", () => {
      itemsInIndex.forEach((item) => {
        const element = document.getElementById(item.id)
        const { top: itemTop } = element?.getBoundingClientRect() ?? {}

        // check if item is in view from the top and bottom
        if (itemTop && window.innerHeight / 3.5 > itemTop) {
          setActiveItem(item.id)
        }
      })
    })
  }, [itemsInIndex])

  useEffect(() => {
    if (inView)
      track({
        event: "Page Element Viewed",
        properties: {
          module_content: "PDP body index",
        },
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  return (
    <ItemsWrapperDiv ref={ref}>
      <ItemsWrapper as="ul" ref={indexRef}>
        {itemsInIndex.map((item) => (
          <IndexItem key={item.id} item={item} activeItem={activeItem} />
        ))}
      </ItemsWrapper>
    </ItemsWrapperDiv>
  )
}
export const PDPBodyIndex = withErrorBoundary(PDPBodyIndexComponent, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`PdpBodyIndex failed to load. ${error}`)
  },
})
